// api.ts — TanStack Query hooks for V2 Stock Inward endpoints.
// All server data flows through here; components never fetch directly.
// Targets: /api/stocks/inward/v2/ (InwardV2ViewSet)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useActiveStoreId } from '@/api/useStores';
import type {
  CreateInwardPayload,
  DiscrepancyCaseDto,
  InwardDetailResponse,
  InwardState,
  InwardDocumentDto,
  InwardHeaderDto,
  InwardLineDto,
  InwardStatsDto,
  PaginatedResponse,
  PostResult,
  ProductSearchResponse,
  ResolveDiscrepancyPayload,
  ScanLookupResponse,
  UpsertLinePayload,
} from './types';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const inwardV2Keys = {
  all: ['stocks', 'inward-v2'] as const,
  list: (params?: object) => [...inwardV2Keys.all, 'list', params] as const,
  detail: (id: string) => [...inwardV2Keys.all, 'detail', id] as const,
  stats: () => [...inwardV2Keys.all, 'stats'] as const,
  products: (q: string) => [...inwardV2Keys.all, 'products', q] as const,
};

const BASE = '/api/stocks/inward/v2';

// ─── Queries ──────────────────────────────────────────────────────────────────

export interface InwardListParams {
  page?: number;
  page_size?: number;
  inward_state?: string;
  store_id?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export function useInwardList(params: InwardListParams = {}) {
  const storeId = useActiveStoreId();
  const queryParams = { ...params, store_id: params.store_id || storeId || undefined };
  return useQuery({
    queryKey: inwardV2Keys.list(queryParams),
    queryFn: () =>
      apiFetch<PaginatedResponse<InwardHeaderDto>>(`${BASE}/`, {
        query: queryParams as Record<string, string | number>,
      }),
  });
}

export function useInwardDetail(id: string) {
  return useQuery({
    queryKey: inwardV2Keys.detail(id),
    queryFn: () => apiFetch<InwardDetailResponse>(`${BASE}/${id}/`),
    enabled: !!id,
  });
}

export function useInwardStats() {
  const storeId = useActiveStoreId();
  const query = storeId ? { store_id: storeId } : undefined;
  return useQuery({
    queryKey: [...inwardV2Keys.stats(), storeId ?? 'all'],
    queryFn: () => apiFetch<InwardStatsDto>(`${BASE}/stats/`, { query }),
  });
}

/** The one product search (A4): names, dm+d codes, PIP/GTIN and the store's own SKU, ranked
 *  exact-first then by what the store holds. ``storeId`` defaults to the active store. */
export function useProductSearch(
  query: string,
  page = 1,
  level: '' | 'vmp' | 'ampp' | 'vmpp' | 'cnd' = '',
  prescribable = false,
  storeId?: string,
) {
  const activeStoreId = useActiveStoreId();
  const store = storeId ?? activeStoreId;
  return useQuery({
    queryKey: inwardV2Keys.products(`${query}|${page}|${level}|${prescribable}|${store ?? ''}`),
    queryFn: () =>
      apiFetch<ProductSearchResponse>('/api/stocks/products/search/', {
        query: {
          q: query,
          page,
          page_size: 50,
          ...(level ? { level } : {}),
          ...(prescribable ? { prescribable: true } : {}),
          ...(store ? { store_id: store } : {}),
        },
      }),
    enabled: query.length >= 2,
  });
}

/** Picker label for a search hit — marks what this store holds (those rank first). */
export function productSearchLabel(r: { display_name: string; stocked?: boolean }): string {
  return r.stocked ? `${r.display_name} · in stock` : r.display_name;
}

/** The store's suggested DEFAULT batch number + expiry for goods-in that has none (initial stock
 *  loading, non-GS1 barcodes). Peeks (no counter change) — used to pre-fill the batch/expiry inputs
 *  so the operator always sees a default; the backend re-fills authoritatively at commit. */
export interface BatchDefaultResponse {
  batch_number: string;
  expiry_date: string;
  auto_default_batch: boolean;
}
/** Goods-in context for one product at a store: current batches (with status) + a suggested bin
 *  (from where it was previously binned, preferring space + no expired stock). Powers bin
 *  auto-select and batch-awareness on the scan / manual / verify / import surfaces. */
export interface ProductBatchInfo {
  batch_number: string;
  expiry_date: string | null;
  qty_on_hand: number;
  qty_free_hold: number;
  bin_location: string;
  status: 'ok' | 'expiring' | 'expired';
}
export interface ProductBinInfo {
  code: string;
  capacity: number;
  occupancy: number;
  space_left: number | null;
  has_expired: boolean;
}
export interface ProductContextResponse {
  suggested_bin: string;
  suggested_reason: string;
  batches: ProductBatchInfo[];
  bins: ProductBinInfo[];
}
export function fetchProductContext(
  storeId: string,
  { sku, snapshotId }: { sku?: string; snapshotId?: string },
): Promise<ProductContextResponse> {
  return apiFetch<ProductContextResponse>('/api/stocks/inward/v2/product-context/', {
    query: { store_id: storeId, sku: sku || '', snapshot_id: snapshotId || '' },
  });
}

export function fetchBatchDefault(storeId: string): Promise<BatchDefaultResponse> {
  // GET PEEKS the next number WITHOUT advancing the counter — opening the form or re-picking a
  // product must never burn a sequence number. The value is derived from stock already saved, so it
  // only moves forward when a line is actually committed; the batch shown is submitted as-is.
  return apiFetch<BatchDefaultResponse>('/api/inventory/batch-default/', {
    query: { store_id: storeId },
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useCreateInwardV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInwardPayload) =>
      apiFetch<InwardHeaderDto>(`${BASE}/`, { method: 'POST', body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function useUpdateInward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<CreateInwardPayload>) =>
      apiFetch<InwardHeaderDto>(`${BASE}/${id}/`, { method: 'PATCH', body }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(id) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function useUpsertLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, lines }: { inwardId: string; lines: UpsertLinePayload[] }) =>
      apiFetch<{ lines: InwardLineDto[] }>(`${BASE}/${inwardId}/lines/`, {
        method: 'POST',
        body: { lines },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useUpdateLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      lineId,
      ...body
    }: { inwardId: string; lineId: string } & Partial<UpsertLinePayload>) =>
      apiFetch<InwardLineDto>(`${BASE}/${inwardId}/lines/${lineId}/`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useDeleteLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, lineId }: { inwardId: string; lineId: string }) =>
      apiFetch<void>(`${BASE}/${inwardId}/lines/${lineId}/delete/`, { method: 'DELETE' }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function useVerifyLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      lineId,
      barcode_raw,
      bin_id,
    }: {
      inwardId: string;
      lineId: string;
      barcode_raw: string;
      bin_id?: string;
    }) =>
      apiFetch<InwardLineDto>(`${BASE}/${inwardId}/lines/${lineId}/verify/`, {
        method: 'POST',
        body: { barcode_raw, bin_id },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useScanLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      lineId,
      scanned_qty = 1,
    }: {
      inwardId: string;
      lineId: string;
      scanned_qty?: number;
    }) =>
      apiFetch<InwardLineDto>(`${BASE}/${inwardId}/lines/${lineId}/scan/`, {
        method: 'POST',
        body: { scanned_qty },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useScanComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, lineId }: { inwardId: string; lineId: string }) =>
      apiFetch<InwardLineDto>(`${BASE}/${inwardId}/lines/${lineId}/scan-complete/`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useQuickVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      lineId,
      verified_qty,
      product_snapshot_id,
      sku,
      batch_number,
      expiry_date,
      bin_id,
      bin_allocations,
      units_per_pack,
      pack_size_reason,
    }: {
      inwardId: string;
      lineId: string;
      verified_qty: number;
      product_snapshot_id?: string | null;
      sku?: string;
      batch_number?: string;
      expiry_date?: string | null;
      bin_id?: string | null;
      bin_allocations?: { bin_id: string; qty: number }[] | null;
      units_per_pack?: number;
      pack_size_reason?: string;
    }) =>
      apiFetch<InwardLineDto & { inward_state?: InwardState }>(
        `${BASE}/${inwardId}/lines/${lineId}/quick-verify/`,
        {
          method: 'POST',
          body: {
            verified_qty,
            product_snapshot_id,
            sku,
            batch_number,
            expiry_date,
            bin_id,
            bin_allocations,
            units_per_pack,
            pack_size_reason,
          },
        },
      ),
    // C6: ≤ 2 requests per line (scan lookup + this verify). The response carries the line and the
    // delivery's new state, so patch the cached delivery instead of re-reading it and the list. A
    // count that differs from the note opens a discrepancy case server-side — re-read then.
    onSuccess: (line, { inwardId }) => {
      const detailKey = inwardV2Keys.detail(inwardId);
      const cached = qc.getQueryData<InwardDetailResponse>(detailKey);
      const counted = line.verified_qty === line.dn_qty;
      if (cached && counted) {
        qc.setQueryData<InwardDetailResponse>(detailKey, {
          ...cached,
          inward_state: line.inward_state ?? cached.inward_state,
          lines: cached.lines.map((l) => (l.id === line.id ? { ...l, ...line } : l)),
        });
      } else {
        qc.invalidateQueries({ queryKey: detailKey });
      }
      // The delivery list is not on screen — mark it stale, refetch when it is next shown.
      qc.invalidateQueries({ queryKey: inwardV2Keys.all, refetchType: 'none' });
    },
  });
}

/** C5: confirm every unverified line arrived exactly as the delivery note says — one explicit,
 *  audited action. Lines that need a check (no batch/expiry, pack size unsettled) come back in
 *  `skipped` with a reason; counted lines keep their count. */
export function useVerifyAllAsDelivered() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inwardId: string) =>
      apiFetch<{ verified: string[]; skipped: { line_id: string; reason: string }[] }>(
        `${BASE}/${inwardId}/verify-all/`,
        { method: 'POST', body: { as_delivered: true } },
      ),
    onSuccess: (_, inwardId) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function useSubmitForValidation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inwardId: string) =>
      apiFetch<InwardHeaderDto>(`${BASE}/${inwardId}/submit/`, { method: 'POST' }),
    onSuccess: (_, inwardId) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function usePostInwardV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, line_ids }: { inwardId: string; line_ids?: string[] }) =>
      apiFetch<PostResult>(`${BASE}/${inwardId}/post/`, {
        method: 'POST',
        body: { line_ids: line_ids ?? null },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

// ─── Direct Import (quick receive) ────────────────────────────────────────────

export interface QuickReceiveBinAllocation {
  bin_code: string;
  packs: number;
}

export interface QuickReceivePayload {
  store_id: string;
  sku: string;
  product_snapshot_id?: string | null;
  batch_number: string;
  expiry_date: string;
  qty_packs: number;
  units_per_pack: number;
  bin_allocations: QuickReceiveBinAllocation[];
  capture_source?: 'barcode' | 'csv';
}

export interface QuickReceiveResult {
  committed: {
    batch_id: string;
    sku: string;
    batch_number: string;
    expiry_date: string;
    qty_on_hand: number;
    bin_location: string;
    units_per_pack: number | null;
  }[];
}

/** Direct Import: commit a scanned line straight to inventory (no GRN). Refetches inventory. */
export function useQuickReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: QuickReceivePayload) =>
      apiFetch<QuickReceiveResult>(`${BASE}/quick-receive/`, {
        method: 'POST',
        body: payload,
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export interface LedgerEntry {
  id: string;
  sku: string;
  product_name: string;
  batch_number: string;
  qty_delta: number;
  source: string;
  notes: string;
  created_at: string;
  created_by: string | null;
}

// The ledger endpoint uses DRF's default PageNumberPagination shape (count/next/previous/results).
export interface LedgerPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: LedgerEntry[];
}

/** Paginated "Completed" Direct Import history from the append-only ledger (the audit record). */
export function useDirectImportHistory(params: {
  storeId?: string;
  noteContains?: string;
  page: number;
}) {
  return useQuery({
    queryKey: ['direct-import-history', params.storeId, params.noteContains, params.page],
    queryFn: () => {
      const query: Record<string, string | number> = {
        source: 'direct_import',
        page: params.page,
      };
      if (params.storeId) query.store_id = params.storeId;
      if (params.noteContains) query.note_contains = params.noteContains;
      return apiFetch<LedgerPage>('/api/stocks/ledger/', { query });
    },
    enabled: Boolean(params.storeId),
  });
}

// ── Direct Import staging: the shared server-side pending list (web scan, Excel, mobile) ──

export interface StagedLine {
  id: string;
  sku: string;
  product_name: string;
  product_snapshot_id: string | null;
  gtin: string;
  controlled: boolean;
  is_fridge: boolean;
  batch_number: string;
  expiry_date: string | null;
  qty_packs: number;
  units_per_pack: number;
  units: number;
  bin_allocations: QuickReceiveBinAllocation[];
  capture_source: 'barcode' | 'csv' | 'mobile' | 'manual';
  status: 'staged' | 'committed' | 'error';
  error_message: string;
  created_by: string | null;
  created_at: string;
  committed_at: string | null;
}

export interface StagedPage {
  results: StagedLine[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface StagePayload {
  store_id: string;
  sku?: string;
  gtin?: string;
  product_snapshot_id?: string | null;
  controlled?: boolean;
  is_fridge?: boolean;
  batch_number: string;
  expiry_date: string;
  /** True when expiry_date is the auto-filled store default (no real expiry captured). */
  expiry_is_default?: boolean;
  qty_packs: number;
  units_per_pack: number;
  bin_allocations: QuickReceiveBinAllocation[];
  capture_source?: 'barcode' | 'csv' | 'mobile' | 'manual';
}

const DI_STAGE_BASE = '/api/stocks/direct-import';
export const stagedKey = (storeId?: string) => ['direct-import-staged', storeId] as const;

/** The shared staged list (pending + errored rows) for a store — web scan, Excel and mobile feed it. */
export function useStagedList(storeId?: string) {
  return useQuery({
    queryKey: stagedKey(storeId),
    queryFn: () =>
      apiFetch<StagedPage>(`${DI_STAGE_BASE}/`, {
        query: { page_size: 200, ...(storeId ? { store_id: storeId } : {}) },
      }),
    enabled: Boolean(storeId),
  });
}

/** Stage one line (scan/mobile). Invalidates the shared staged list. */
export function useStageDirectImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StagePayload) =>
      apiFetch<StagedLine>(`${DI_STAGE_BASE}/`, { method: 'POST', body: payload }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: stagedKey(v.store_id) }),
  });
}

export function useEditStaged() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      store_id: _s,
      ...body
    }: { id: string; store_id: string } & Partial<StagePayload>) =>
      apiFetch<StagedLine>(`${DI_STAGE_BASE}/${id}/`, { method: 'PATCH', body }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: stagedKey(v.store_id) }),
  });
}

export function useRemoveStaged() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; store_id: string }) =>
      apiFetch<void>(`${DI_STAGE_BASE}/${id}/`, { method: 'DELETE' }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: stagedKey(v.store_id) }),
  });
}

export interface CommitStagedResult {
  committed: StagedLine[];
  failed: StagedLine[];
  committed_count: number;
  failed_count: number;
}

/** "Add to Stock": commit staged rows to inventory. Refetches staged list, inventory + history. */
export function useCommitStaged() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ store_id, ids }: { store_id: string; ids?: string[] }) =>
      apiFetch<CommitStagedResult>(`${DI_STAGE_BASE}/commit/`, {
        method: 'POST',
        body: { store_id, ...(ids ? { ids } : {}) },
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: stagedKey(v.store_id) });
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['direct-import-history'] });
    },
  });
}

export function useVoidInwardV2() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, reason = '' }: { inwardId: string; reason?: string }) =>
      apiFetch<InwardHeaderDto>(`${BASE}/${inwardId}/void/`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
      qc.invalidateQueries({ queryKey: inwardV2Keys.all });
    },
  });
}

export function useResolveDiscrepancy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      caseId,
      ...body
    }: { inwardId: string; caseId: string } & ResolveDiscrepancyPayload) =>
      apiFetch<DiscrepancyCaseDto>(`${BASE}/${inwardId}/discrepancies/${caseId}/resolve/`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useCreateDiscrepancy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      line_id,
      case_type,
      actual_qty,
      notes,
    }: {
      inwardId: string;
      line_id: string;
      case_type: string;
      actual_qty: number;
      notes?: string;
    }) =>
      apiFetch<DiscrepancyCaseDto>(`${BASE}/${inwardId}/discrepancies/create/`, {
        method: 'POST',
        body: { line_id, case_type, actual_qty, notes },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useEditDiscrepancy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      caseId,
      ...body
    }: {
      inwardId: string;
      caseId: string;
      case_type?: string;
      actual_qty?: number;
      notes?: string;
    }) =>
      apiFetch<DiscrepancyCaseDto>(`${BASE}/${inwardId}/discrepancies/${caseId}/edit/`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useDeleteDiscrepancy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ inwardId, caseId }: { inwardId: string; caseId: string }) =>
      apiFetch<void>(`${BASE}/${inwardId}/discrepancies/${caseId}/delete/`, {
        method: 'DELETE',
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useNotifySupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      caseId,
      supplier_email,
    }: {
      inwardId: string;
      caseId: string;
      supplier_email: string;
    }) =>
      apiFetch<DiscrepancyCaseDto>(`${BASE}/${inwardId}/discrepancies/${caseId}/notify-supplier/`, {
        method: 'POST',
        body: { supplier_email },
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      inwardId,
      ...body
    }: {
      inwardId: string;
      doc_type: string;
      file_url: string;
      file_name: string;
    }) =>
      apiFetch<InwardDocumentDto>(`${BASE}/${inwardId}/documents/`, {
        method: 'POST',
        body,
      }),
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

/** Upload a file directly (multipart/form-data) to Azure via backend. */
export function useUploadDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      inwardId,
      file,
      doc_type,
      file_name,
    }: {
      inwardId: string;
      file: File;
      doc_type: string;
      file_name: string;
    }) => {
      const { getAuthToken } = await import('@/api/client');
      const token = await getAuthToken();
      const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
      const url = `${BASE_URL.replace(/\/?$/, '/')}${BASE.replace(/^\//, '')}/${inwardId}/documents/upload/`;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', doc_type);
      formData.append('file_name', file_name);

      const headers: HeadersInit = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url, { method: 'POST', headers, body: formData });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error((errBody as any)?.detail || `Upload failed (${res.status})`);
      }
      return res.json() as Promise<InwardDocumentDto>;
    },
    onSuccess: (_, { inwardId }) => {
      qc.invalidateQueries({ queryKey: inwardV2Keys.detail(inwardId) });
    },
  });
}

export function useScanLookup() {
  return useMutation({
    mutationFn: ({ barcode_raw, store_id }: { barcode_raw: string; store_id: string }) =>
      apiFetch<ScanLookupResponse>(`${BASE}/scan-lookup/`, {
        method: 'POST',
        body: { barcode_raw, store_id },
      }),
  });
}

export function useInwardMovements(inwardId: string) {
  return useQuery({
    queryKey: [...inwardV2Keys.detail(inwardId), 'movements'],
    queryFn: () => apiFetch<unknown[]>(`${BASE}/${inwardId}/movements/`),
    enabled: !!inwardId,
  });
}

export function useInwardDocuments(inwardId: string) {
  return useQuery({
    queryKey: [...inwardV2Keys.detail(inwardId), 'documents'],
    queryFn: () => apiFetch<InwardDocumentDto[]>(`${BASE}/${inwardId}/documents/`),
    enabled: !!inwardId,
  });
}
